import React from 'react';
import { openExternal } from '../../utils/openExternal';

/**
 * Inline Markdown → React nodes for the docs dialect: `code`, <kbd>…</kbd>,
 * [[page#heading]] internal links, [label](https://…) external links,
 * **bold**, *em* / _em_. Everything else is text (React escapes it).
 *
 * ctx.navigate(target) opens an internal link; ctx.linkLabel(target) names it.
 */
const TOKEN =
  /(`[^`]+`)|<kbd>(.*?)<\/kbd>|\[\[([^\]]+)\]\]|\[([^\]]+)\]\(([^)\s]+)\)|\*\*(.+?)\*\*|\*([^*\n]+)\*|(^|[^\w])_([^_\n]+)_(?![\w])/g;

export function renderInline(text, ctx = {}, keyPrefix = 'i') {
  const out = [];
  const src = String(text || '');
  let last = 0;
  let n = 0;
  // A fresh regex per call: the renderer recurses into bold/em/link labels,
  // and a shared /g regex would have its lastIndex reset under it.
  const re = new RegExp(TOKEN.source, 'g');
  let m;
  while ((m = re.exec(src))) {
    // `_em_` captures the character before it (no lookbehind: older WebKit
    // cannot parse one); keep that character as text.
    const lead = m[9] !== undefined ? m[8] : '';
    const start = m.index + lead.length;
    if (start > last) out.push(src.slice(last, start));
    const key = `${keyPrefix}-${n++}`;
    if (m[1]) {
      out.push(<code key={key}>{m[1].slice(1, -1)}</code>);
    } else if (m[2] !== undefined) {
      out.push(<kbd key={key}>{m[2]}</kbd>);
    } else if (m[3]) {
      const target = m[3].trim();
      out.push(
        <button key={key} type="button" className="help-link" onClick={() => ctx.navigate?.(target)}>
          {ctx.linkLabel ? ctx.linkLabel(target) : target}
        </button>
      );
    } else if (m[4]) {
      const href = m[5];
      if (/^https?:\/\//i.test(href)) {
        out.push(
          <button key={key} type="button" className="help-link" onClick={() => openExternal(href)}>
            {renderInline(m[4], ctx, key)}
          </button>
        );
      } else {
        out.push(<React.Fragment key={key}>{renderInline(m[4], ctx, key)}</React.Fragment>);
      }
    } else if (m[6]) {
      out.push(<strong key={key}>{renderInline(m[6], ctx, key)}</strong>);
    } else if (m[7] || m[9]) {
      out.push(<em key={key}>{renderInline(m[7] || m[9], ctx, key)}</em>);
    }
    last = re.lastIndex;
  }
  if (last < src.length) out.push(src.slice(last));
  return out;
}

/** Plain text of an inline string (for heading ids, search, labels). */
export function inlineText(text) {
  return String(text || '')
    .replace(/<kbd>(.*?)<\/kbd>/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*([^*\n]+)\*/g, '$1');
}
